import EditorOutput from '@/components/common/EditorOutput';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { CaretIcon, ModalCloseButton, Select } from '@frabbit/react-responsive-select';
import Map from './DottedMap';
import styles from './geo.module.scss';
import data from './map.json';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

const variants = {
  initial: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 1.3,
      delay: 1.5,
    },
  },
  hidden: { opacity: 0 },
};

const Geo = ({ pageData = { map: [], categories: [] } }) => {
  const { t } = useTranslation(['common', 'geo']);
  const router = useRouter();
  const [countries] = useState(pageData.map);
  const [categories] = useState(pageData.categories);
  const [active, setActive] = useState(null);

  const products = useMemo(() => {
    return categories.reduce((acc, cat) => [...acc, ...cat.products], []);
  }, [categories]);

  const current = useMemo(
    () => (active ? countries.filter(c => c.products.some(p => p.id === active)) : null),
    [active, countries]
  );

  const options = useMemo(() => {
    return [
      ...categories.reduce((acc, category) => {
        return [
          ...acc,
          {
            optHeader: true,
            text: category.title,
            markup: <span className={styles.selectGroupName}>{category.title}</span>,
          },
          ...category.products.map(child => ({
            value: child.id,
            text: child.title,
            markup: (
              <div className={styles.selectItem}>
                {child.icon && <img src={child.icon} alt={child.title} />}
                <span>{child.title}</span>
              </div>
            ),
          })),
        ];
      }, []),
    ];
  }, [categories]);

  const SelectMemo = useMemo(
    () => (
      <Select
        name="categories"
        noSelectionLabel={t('Продукция')}
        modalCloseButton={<ModalCloseButton />}
        options={options}
        caretIcon={<CaretIcon />}
        onChange={({ value }) => setActive(value)}
      />
    ),
    [setActive, options, router]
  );

  return (
    <>
      <div className={styles.header}>
        {pageData.text && <EditorOutput data={pageData.text} />}
        <motion.div
          className={styles.select}
          initial="initial"
          animate="visible"
          exit="hidden"
          variants={variants}
        >
          {SelectMemo}
          {active && (
            <div className={styles.goToProduct}>
              <Link href={`/catalog/product/${active}`}>
                <a>
                  <motion.span
                    initial={{
                      opacity: 0,
                      x: '-24px',
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      transition: { duration: 1.1, delay: 1.8 },
                    }}
                    exit={{
                      opacity: 0,
                      y: '-15%',
                      transition: { duration: 0.6 },
                    }}
                  >
                    {t('Перейти к продукту')}
                  </motion.span>
                  <motion.span
                    className={styles.arrow}
                    initial={{
                      opacity: 0,
                      x: '16px',
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      transition: { duration: 1.1, delay: 1.8 },
                    }}
                    exit={{
                      opacity: 0,
                      y: '-15%',
                      transition: { duration: 0.6 },
                    }}
                  />
                </a>
              </Link>
            </div>
          )}
        </motion.div>
      </div>
      <motion.div
        className={styles.mapWrapper}
        initial="initial"
        animate="visible"
        exit="hidden"
        variants={variants}
      >
        <Map simple={true} pins={current ? current : countries} data={data} />
      </motion.div>
    </>
  );
};

export default Geo;
